import { type ReactNode, createContext, useMemo } from 'react'

import { type FrameBuilderConfigurationWithLimits } from '../lib/configuration'
import {
  getMaximumProfileHeight,
  getMaximumProfileWidth,
  getMinimumProfileSize,
} from '../lib/size'
import {
  type FrameBuilderExtraService,
  type FrameBuilderGlass,
  type FrameBuilderPassepartoutColor,
  type FrameBuilderPassepartoutSize,
  type FrameBuilderProfile,
  type FrameBuilderServicePrices,
  type FrameBuilderSpacerColor,
  type FrameBuilderSpacerSize,
  type FrameBuilderStandardSize,
  type FrameBuilderTexture,
} from '../lib/state'

interface FrameBuilderConfigurationProviderProps {
  standardSizes: FrameBuilderStandardSize[]
  profiles: FrameBuilderProfile[]
  textures: FrameBuilderTexture[]
  passepartoutSizes: FrameBuilderPassepartoutSize[]
  passepartoutColors: FrameBuilderPassepartoutColor[]
  glass: FrameBuilderGlass[]
  spacerSizes: FrameBuilderSpacerSize[]
  spacerColors: FrameBuilderSpacerColor[]
  extraServices: FrameBuilderExtraService[]
  servicePrices: FrameBuilderServicePrices
  children: ReactNode
}

export const FrameBuilderConfigurationContext =
  createContext<FrameBuilderConfigurationWithLimits>({
    textures: [],
    profiles: [],
    passepartout: {
      sizes: [],
      colors: [],
    },
    glass: [],
    extraServices: [],
    spacer: {
      sizes: [],
      colors: [],
    },
    servicePrices: {
      backBoard: 0,
      backBoardMinimum: 0,
      cutWaste: 0,
      productionFee: 0,
      largeFrameProductionFee: 0,
      passepartout: 0,
      passepartoutMinimum: 0,
    },
    standardSizes: [],
    limits: {
      minimumSize: 1,
      maximumWidth: 1000,
      maximumHeight: 1000,
    },
  })

export const FrameBuilderConfigurationProvider = ({
  standardSizes,
  profiles,
  textures,
  passepartoutSizes,
  passepartoutColors,
  glass,
  spacerSizes,
  spacerColors,
  extraServices,
  servicePrices,
  children,
}: FrameBuilderConfigurationProviderProps) => {
  const configuration = useMemo<FrameBuilderConfigurationWithLimits>(() => {
    const minimumSize = getMinimumProfileSize(profiles)
    const maximumWidth = getMaximumProfileWidth(profiles)
    const maximumHeight = getMaximumProfileHeight(profiles)

    return {
      profiles,
      textures,
      passepartout: {
        sizes: passepartoutSizes,
        colors: passepartoutColors,
      },
      glass,
      extraServices,
      spacer: {
        sizes: spacerSizes,
        colors: spacerColors,
      },
      servicePrices,
      standardSizes,
      limits: {
        minimumSize,
        maximumWidth,
        maximumHeight,
      },
    }
  }, [
    extraServices,
    glass,
    passepartoutColors,
    passepartoutSizes,
    profiles,
    servicePrices,
    spacerColors,
    spacerSizes,
    standardSizes,
    textures,
  ])

  return (
    <FrameBuilderConfigurationContext.Provider value={configuration}>
      {children}
    </FrameBuilderConfigurationContext.Provider>
  )
}
